import {
  calculateLeaseValuation,
  type LeaseValuationResult,
} from "../financing/lease-valuation";
import { lookupRuleOfThumb, type RuleOfThumbEntry } from "../data/rule-of-thumb-multiples";
import { lookupSectorMultiple, type SectorMultiple } from "../data/sector-multiples";
import { reputationAdjustment } from "../multiples/adjustments";

export type ListingValuationRange = {
  min: number | null;
  max: number | null;
  tier: "PROFESSIONAL" | "QUICK" | "INDUSTRY";
  confidenceScore?: number | null;
};

export type ListingPriceContextInput = {
  industry?: string | null;
  askingPrice?: number | null;
  propertyAskingPrice?: number | null;
  revenue?: number | null;
  profit?: number | null;
  sde?: number | null;
  yearsOperating?: number | null;
  establishedAt?: Date | string | null;
  leaseExpiration?: Date | string | null;
  valuationRange?: ListingValuationRange | null;
};

export type ListingPriceContext = {
  totalPrice: number | null;
  revenueMultiple: number | null;
  sdeMultiple: number | null;
  profitMargin: number | null;
  yearsOperating: number | null;
  reputationAdjustment: number | null;
  leaseMonthsRemaining: number | null;
  leaseRisk: LeaseValuationResult["riskRating"] | null;
  valuationPosition: "below" | "within" | "above" | null;
  valuationRange: ListingValuationRange | null;
  sectorMultiple: SectorMultiple | null;
  ruleOfThumb: RuleOfThumbEntry | null;
};

function positiveNumber(value: number | null | undefined): number | null {
  return value != null && Number.isFinite(value) && value > 0 ? value : null;
}

function resolveYearsOperating(
  yearsOperating: number | null | undefined,
  establishedAt: Date | string | null | undefined,
): number | null {
  if (yearsOperating != null && yearsOperating >= 0) return yearsOperating;
  if (!establishedAt) return null;

  const started = new Date(establishedAt);
  if (Number.isNaN(started.getTime())) return null;
  return Math.max(0, new Date().getFullYear() - started.getFullYear());
}

function monthsUntil(date: Date | string | null | undefined): number | null {
  if (!date) return null;
  const expiration = new Date(date);
  if (Number.isNaN(expiration.getTime())) return null;
  return Math.round(
    (expiration.getTime() - Date.now()) / (1000 * 60 * 60 * 24 * 30),
  );
}

export function getListingPriceContext(
  input: ListingPriceContextInput,
): ListingPriceContext {
  const askingPrice = positiveNumber(input.askingPrice);
  const propertyPrice = positiveNumber(input.propertyAskingPrice);
  const revenue = positiveNumber(input.revenue);
  const sde = positiveNumber(input.sde);
  const profit = positiveNumber(input.profit);
  const totalPrice =
    askingPrice == null && propertyPrice == null
      ? null
      : (askingPrice ?? 0) + (propertyPrice ?? 0);
  const years = resolveYearsOperating(input.yearsOperating, input.establishedAt);
  const leaseMonths = monthsUntil(input.leaseExpiration);
  const sectorMultiple = input.industry
    ? lookupSectorMultiple(input.industry) ?? null
    : null;
  const ruleOfThumb = input.industry
    ? lookupRuleOfThumb(input.industry) ?? null
    : null;

  let leaseRisk: LeaseValuationResult["riskRating"] | null = null;
  if (leaseMonths != null && leaseMonths > 0) {
    leaseRisk = calculateLeaseValuation({
      currentMonthlyRent: 0,
      marketMonthlyRent: 0,
      remainingLeaseMonths: leaseMonths,
    }).riskRating;
  }

  let valuationPosition: ListingPriceContext["valuationPosition"] = null;
  const range = input.valuationRange;
  if (totalPrice != null && range?.min != null && range.max != null) {
    valuationPosition =
      totalPrice < range.min
        ? "below"
        : totalPrice > range.max
          ? "above"
          : "within";
  }

  return {
    totalPrice,
    revenueMultiple:
      totalPrice != null && revenue != null ? totalPrice / revenue : null,
    sdeMultiple: totalPrice != null && sde != null ? totalPrice / sde : null,
    profitMargin: revenue != null && profit != null ? profit / revenue : null,
    yearsOperating: years,
    reputationAdjustment:
      years != null ? reputationAdjustment(years) : null,
    leaseMonthsRemaining: leaseMonths,
    leaseRisk,
    valuationPosition,
    valuationRange: range ?? null,
    sectorMultiple,
    ruleOfThumb,
  };
}
